import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { Account } from './types';
import { ACCOUNT_LIST_ITEM_HEIGHT, AccountListItem } from './AccountListItem';
import { FitScoreSlider } from '../ui/FitScoreSlider';
import { InfoHint } from '../ui/InfoHint';
import { FIT_SCORE_HELP, PROPENSITY_SCORE_HELP } from '../../lib/scoreHelp';

const ROW_HEIGHT = ACCOUNT_LIST_ITEM_HEIGHT;

interface AccountSidebarProps {
  accounts: Account[];
  selectedAccountId: string | null;
  search: string;
  showStarredOnly: boolean;
  onSelectAccount: (accountId: string) => void | Promise<unknown>;
  onSearchChange: (search: string) => void;
  onToggleStarredOnly: () => void;
  onToggleStar: (accountId: string) => void;
  isLoading?: boolean;
}

/**
 * Account sidebar component that displays a searchable, filterable list of accounts.
 * Purely presentational - all state and handlers come from props.
 * Fit score filter is local state (no URL persistence needed).
 */
export function AccountSidebar({
  accounts,
  selectedAccountId,
  search,
  showStarredOnly,
  onSelectAccount,
  onSearchChange,
  onToggleStarredOnly,
  onToggleStar,
  isLoading = false,
}: AccountSidebarProps) {
  const [minFitScore, setMinFitScore] = useState(0);
  const [includeUnscored, setIncludeUnscored] = useState(true);

  const hasAnyFitScores = useMemo(
    () => accounts.some((a) => a.fitScore != null),
    [accounts],
  );

  const filteredAccounts = useMemo(() => {
    return accounts
      .filter((a) => a.accountName.toLowerCase().includes(search.toLowerCase()))
      .filter((a) => !showStarredOnly || a.isStarred)
      .filter((a) => {
        // When the loaded set has no fit scores, the slider is hidden; skip
        // the filter entirely so residual state can't silently empty the
        // list (common in show-all mode where most SF rows lack a score).
        if (!hasAnyFitScores) return true;
        if (minFitScore === 0) return true;
        if (a.fitScore == null) return includeUnscored;
        return a.fitScore >= minFitScore;
      });
  }, [accounts, search, showStarredOnly, minFitScore, includeUnscored, hasAnyFitScores]);

  // Virtualize the account list so rendering stays O(viewport) regardless of
  // whether the user has 20 or 8,000 accounts. Fixed row height avoids the
  // measureElement cost — AccountListItem is height-clamped.
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: filteredAccounts.length,
    getScrollElement: useCallback(() => scrollRef.current, []),
    estimateSize: useCallback(() => ROW_HEIGHT, []),
    overscan: 8,
    getItemKey: useCallback(
      (index: number) => filteredAccounts[index]?.accountId ?? index,
      [filteredAccounts],
    ),
  });

  // Keep the selected account visible when it falls outside the virtual
  // window — e.g. after a filter change narrows the list or the user navigates
  // to an offscreen account via keyboard/URL. align: 'auto' means no scroll
  // when the row is already visible.
  useEffect(() => {
    if (!selectedAccountId) return;
    const idx = filteredAccounts.findIndex((a) => a.accountId === selectedAccountId);
    if (idx >= 0) virtualizer.scrollToIndex(idx, { align: 'auto' });
  }, [selectedAccountId, filteredAccounts, virtualizer]);

  const showEmptyState = !isLoading && filteredAccounts.length === 0;
  const emptyMessage = accounts.length === 0
    ? 'No accounts available'
    : search
      ? 'No accounts match your search'
      : showStarredOnly
        ? 'No starred accounts'
        : minFitScore > 0
          ? 'No accounts match this fit score filter'
          : 'No accounts';

  return (
    <>
      {/* Account search and filters - always shown for consistent UI */}
      <div className="flex-none p-2.5 space-y-2">
          <div className="relative">
            <svg className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-icon-subtle" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search..."
              className="w-full pl-7 pr-2 py-1.5 text-xs rounded border border-border-secondary focus:border-matcha-500 focus:ring-1 focus:ring-matcha-500 focus:outline-none placeholder:text-content-placeholder"
            />
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => showStarredOnly && onToggleStarredOnly()}
              className={`flex-1 px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                !showStarredOnly ? 'bg-stone-900 text-white' : 'text-icon-subtle hover:bg-bg-tertiary'
              }`}
            >
              All
            </button>
            <button
              onClick={() => !showStarredOnly && onToggleStarredOnly()}
              className={`flex-1 px-2 py-1 text-[10px] font-medium rounded transition-colors ${
                showStarredOnly ? 'bg-stone-900 text-white' : 'text-icon-subtle hover:bg-bg-tertiary'
              }`}
            >
              Starred
            </button>
            <InfoHint align="right" label="What Fit Score and Propensity Score mean" className="p-1">
              <span className="mb-1.5 block">
                <span className="font-semibold text-content-primary">Fit Score:</span> {FIT_SCORE_HELP}
              </span>
              <span className="block">
                <span className="font-semibold text-content-primary">Propensity Score:</span> {PROPENSITY_SCORE_HELP}
              </span>
            </InfoHint>
          </div>
          {(hasAnyFitScores || isLoading) && (
            <FitScoreSlider
              value={minFitScore}
              includeUnscored={includeUnscored}
              onChange={(v) => setMinFitScore(v ?? 0)}
              onIncludeUnscoredChange={setIncludeUnscored}
            />
          )}
      </div>

      {/* Account list — scroll container renders unconditionally so the
          virtualizer always has a ref'd scroll element, per @tanstack/react-virtual
          guidance. Empty and loading states render inside it. */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto">
        {isLoading ? (
          <div>
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="px-2.5 py-2 border-b border-border-tertiary">
                <div className="flex items-start gap-2">
                  <Skeleton circle width={16} height={16} />
                  <div className="flex-1">
                    <Skeleton width="70%" height={12} />
                    <Skeleton width="40%" height={10} className="mt-1" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : showEmptyState ? (
          <div className="px-2.5 py-4 text-xs text-icon-subtle text-center">
            {emptyMessage}
          </div>
        ) : (
          <div
            className="relative w-full"
            style={{ height: virtualizer.getTotalSize() }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const account = filteredAccounts[virtualRow.index];
              if (!account) return null;
              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  className="absolute left-0 top-0 w-full"
                  style={{
                    height: ROW_HEIGHT,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <AccountListItem
                    account={account}
                    isSelected={account.accountId === selectedAccountId}
                    onSelect={() => { void onSelectAccount(account.accountId); }}
                    onToggleStar={() => onToggleStar(account.accountId)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
